import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Star, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { reportesAPI } from '../services/api';

const formatear = (iso) =>
  new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });

function Estrellas({ valor, size = 'w-4 h-4' }) {
  return (
    <span className="inline-flex" aria-label={`${valor} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= valor ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
      ))}
    </span>
  );
}

// Reseñas (calificaciones del solicitante) de un agente en el periodo del reporte
export default function ResenasAgenteModal({ agente, from, to, onClose }) {
  const navigate = useNavigate();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!agente) return undefined;
    let activo = true;
    setDatos(null);
    setError('');
    reportesAPI.getResenasAgente(agente.id, { from, to })
      .then((r) => { if (activo) setDatos(r.data); })
      .catch(() => { if (activo) setError('No se pudieron cargar las reseñas.'); });
    return () => { activo = false; };
  }, [agente, from, to]);

  useEffect(() => {
    if (!agente) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [agente, onClose]);

  if (!agente) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={`Reseñas de ${agente.nombre}`} className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Reseñas de {agente.nombre}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Período {from} al {to}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {datos && datos.total > 0 && (
          <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{datos.promedio}</span>
            <Estrellas valor={Math.round(datos.promedio)} size="w-5 h-5" />
            <span className="text-sm text-gray-500 dark:text-gray-400">{datos.total} {datos.total === 1 ? 'reseña' : 'reseñas'}</span>
          </div>
        )}

        <div className="overflow-y-auto p-5 space-y-3">
          {!datos && !error && <p className="text-sm text-gray-500 text-center py-6">Cargando…</p>}
          {error && <p className="text-sm text-red-600 text-center py-6">{error}</p>}
          {datos && datos.total === 0 && <p className="text-sm text-gray-500 text-center py-6">Sin reseñas en el período.</p>}
          {datos && datos.resenas.map((r) => (
            <div key={r.ticketId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Estrellas valor={r.calificacion} />
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 truncate">{r.asunto}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{r.solicitante} · {formatear(r.fecha)}</p>
                </div>
                <button
                  onClick={() => { onClose(); navigate(`/tickets/${r.ticketId}`); }}
                  className="shrink-0 text-xs font-semibold text-primary hover:underline"
                >
                  Ticket #{r.ticketId}
                </button>
              </div>
              {(r.rapidez || r.atencion) && (
                <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {r.rapidez ? <span>Rapidez {r.rapidez}/5</span> : null}
                  {r.atencion ? <span>Atención {r.atencion}/5</span> : null}
                </div>
              )}
              {r.comentario && <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap break-words">{r.comentario}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
